/* eslint max-len: 0, comma-dangle: 0, react/no-danger: 0 */

import React from 'react';
import { string, node } from 'prop-types';
import { useRouter } from 'next/router';
import NextHead from 'next/head';

import styles from '@jobcase/react_pattern_library/styles/rebrand';

import { getGoogleTagManagerConfig } from '../utils/getGoogleTagManagerConfig';
import { getNewRelicConfig } from '../utils/getNewRelicConfig';
import config from '../config';

const EXCLUDE_PAGES_FOR_CHAT_ICON = ['/employer-onboarding', '/employer-onboarding-internal', '/employer-onboarding-partners'];

const defaultImage = 'https://s3.amazonaws.com/jobcase_production/Jobcase-Assets/jobcase_logo_bluebg.png';

const globalStyles = isChatIconVisible => `
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: url(https://fonts.gstatic.com/s/inter/v2/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuFuYAZ9hjp-Ek-_EeA.woff) format('woff');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
  @font-face {
    font-family: 'Roboto';
    font-style: italic;
    font-weight: 300;
    font-display: swap;
    src: local('Roboto Light Italic'), local('Roboto-LightItalic'), url(https://fonts.gstatic.com/s/roboto/v20/KFOjCnqEu92Fr1Mu51TjASc6CsTYl4BO.woff2) format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
  @font-face {
    font-family: 'Roboto';
    font-style: italic;
    font-weight: 400;
    font-display: swap;
    src: local('Roboto Italic'), local('Roboto-Italic'), url(https://fonts.gstatic.com/s/roboto/v20/KFOkCnqEu92Fr1Mu51xIIzIXKMny.woff2) format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 300;
    font-display: swap;
    src: local('Roboto Light'), local('Roboto-Light'), url(https://fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmSU5fBBc4AMP6lQ.woff2) format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: local('Roboto'), local('Roboto-Regular'), url(https://fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu4mxKKTU1Kg.woff2) format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 500;
    font-display: swap;
    src: local('Roboto Medium'), local('Roboto-Medium'), url(https://fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmEU9fBBc4AMP6lQ.woff2) format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
  @font-face {
    font-family: 'Founders Grotesk';
    font-style: normal;
    font-weight: 500;
    src: url('/fonts/founders-grotesk/founders-grotesk-web-medium.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 400;
    src: url('/fonts/inter/Inter-Regular.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 600;
    src: url('/fonts/inter/Inter-SemiBold.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Founders Grotesque X-Cond Bold';
    font-style: normal;
    font-weight: 700;
    src: url('/fonts/founders-grotesk/founders-grotesk-x-condensed-web-bold.woff2') format('woff2');
  }

  html {
    height: 100%;
    overflow: auto;
  }
  body {
    height: 100%;
    margin: 0;
    overflow-x: hidden;
  }
  #fc_frame.fc-widget-normal {
    transition: all 250ms cubic-bezier(0.250, 0.250, 0.750, 0.750) !important;
  }
  body.hasFooter #job-action-btn {
    bottom: 98px !important;
  }
  body.hasFooter #fc_frame.fc-widget-normal {
    bottom: 91px !important;
  }
  body.hasFooter .fade-background-enter-done:has(.CandidateDetailOverlay) {
    z-index: 2147483604;
  }
  #__next {
    height: 100%;
  }
  a {
    text-decoration: none;
  }
  * {
    box-sizing: border-box;
    visibility: inherit;
  }

  .fc-mobile-view {
    left: unset;
    right: ${isChatIconVisible ? '11px' : 'unset'};
  }

  @media (max-width: ${styles.breakpoint.md_num - 1}px) {
    .fc-mobile-view {
      left: ${!isChatIconVisible ? '11px' : 'unset'};
      right: ${isChatIconVisible ? '11px' : 'unset'};
      z-index: ${isChatIconVisible ? 9999 : 8995} !important;
    }
  }
`;

const Head = ({
  description,
  image,
  title,
  path,
  children,
}) => {
  const router = useRouter();
  const isChatIconVisible = EXCLUDE_PAGES_FOR_CHAT_ICON.includes(router.pathname);

  return (
    <NextHead>
      <title>{title || ''}</title>

      <style dangerouslySetInnerHTML={{ __html: globalStyles(isChatIconVisible) }} key="globalStyles" />

      <script dangerouslySetInnerHTML={{ __html: getGoogleTagManagerConfig() }} key="gtmConfig" />
      <script dangerouslySetInnerHTML={{ __html: getNewRelicConfig() }} key="newRelicConfig" />

      <script
        key="ldjson"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: `
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Jobcase",
          "url": "https://www.jobcase.com/",
          "logo": "${defaultImage}",
          "sameAs": [
            "https://www.facebook.com/jobcaseinc",
            "https://twitter.com/therealjobcase",
            "https://www.instagram.com/jobcase",
            "https://www.youtube.com/channel/UCAApqRFVsKWEovCJdJSQ1Tg",
            "https://www.linkedin.com/company/jobcase-inc-",
            "https://www.glassdoor.com/Overview/Working-at-Jobcase-EI_IE939570.11,18.htm",
            "https://en.wikipedia.org/wiki/Jobcase_(company)",
            "https://www.bloomberg.com/research/stocks/private/snapshot.asp?privcapid=309249132"
          ]
        }
      ` }}
      />

      <meta charSet="UTF-8" key="charSet" />
      <meta name="viewport" content="width=device-width, initial-scale=1" key="viewport" />
      <meta name="description" content={description} key="description" />
      <meta property="og:type" content="website" key="ogType" />
      <meta property="og:site_name" content="Jobcase" key="ogSiteName" />
      <meta property="og:url" content={`${config.jobcaseUrl}${path}`} key="ogUrl" />
      <meta property="og:title" content={title} key="ogTitle" />
      <meta property="og:description" content={description} key="ogDescription" />
      <meta property="og:image" content={image || defaultImage} key="ogImage" />
      <meta name="twitter:site" content="@therealjobcase" key="twitterSite" />
      <meta name="twitter:card" content="summary_large_image" key="twitterCard" />
      <link rel="canonical" href={`${config.jobcaseUrl}${path}`} key="canonicalUrl" />

      {/* Facebook ads domain verification */}
      <meta name="facebook-domain-verification" content="3o0faj2ylcp0zx47wosordb0m821fc" key="fbAdVerify" />

      <link rel="icon" href="/static-ec/favicon/128x128.png" sizes="128x128" key="favicon128" />
      <link rel="icon" href="/static-ec/favicon/96x96.png" sizes="96x96" key="favicon96" />
      <link rel="icon" href="/static-ec/favicon/64x64.png" sizes="64x64" key="favicon64" />
      <link rel="icon" href="/static-ec/favicon/48x48.png" sizes="48x48" key="favicon48" />
      <link rel="icon" href="/static-ec/favicon/32x32.png" sizes="32x32" key="favicon32" />
      <link rel="icon" href="/static-ec/favicon/24x24.png" sizes="24x24" key="favicon24" />
      <link rel="icon" href="/static-ec/favicon/16x16.png" sizes="16x16" key="favicon16" />

      {/* Smart banner for the native app */}
      <meta name="smartbanner:apple-itunes-app" content="app-id=1506836734" />
      <meta name="smartbanner:google-play-app" content="app-id=com.jobcase.member" />
      <link rel="apple-touch-icon" href="/static-ec/logo-smart-banner.png" />
      <link rel="android-touch-icon" href="/static-ec/logo-smart-banner.png" />
      <link rel="manifest" href="/.well-known/manifest.json" />

      {image && (
      <link rel="preload" href={image} as="image" key="image-preload" />
      )}

      {children}

      <link rel="preload" href="https://fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu4mxKKTU1Kg.woff2" as="font" type="font/woff2" key="roboto-preload" crossOrigin="anonymous" />
      <link rel="preload" href="https://fonts.gstatic.com/s/inter/v2/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuFuYAZ9hjp-Ek-_EeA.woff" as="font" type="font/woff" key="inter-preload" crossOrigin="anonymous" />

      {config.environment !== 'production' && (
      <meta name="robots" content="noindex" key="noindex" />
      )}
    </NextHead>
  );
};

Head.propTypes = {
  title: string,
  description: string,
  path: string,
  image: string,
  children: node,
};

Head.defaultProps = {
  title: '',
  description: '',
  path: '',
  image: '',
  children: null,
};

export default Head;
